<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { messageOf } from '@/utils/messages'
const props = defineProps({ enrollId: { type: Number, required: true }, purpose: { type: String, required: true }, itemId: { type: [String, Number], default: null }, target: { type: String, default: null }, request: { type: Function, required: true } })
const emit = defineEmits(['verified'])
const challenge = ref(null), context = ref(null), otp = ref(''), busy = ref(false), error = ref(''), unavailable = ref(false), retry = ref(false), nice = ref(null), fallback = ref(false)
const base = `/api/user/enrollments/${props.enrollId}/identity/motp`
const alternatives = computed(() => context.value?.alternativeMethods || [])
// kiraedu와 같이 mOTP 설치·사용 안내 게시글로 연결한다.
const MOTP_GUIDE = 'https://techedu.kr/bbs/?boardCode=1&seq=7635'
let alive = true, popup = null, polling = null
// 인증 요청(서버 UUID)을 먼저 만든다. mOTP 번호 확인과 휴대폰·아이핀 인증 모두 이 요청에 묶인다.
async function prepare() {
  if (busy.value) return
  busy.value = true; error.value = ''; otp.value = ''; challenge.value = null; context.value = null; retry.value = false; unavailable.value = false; fallback.value = false
  try { const result = await props.request(`${base}/prepare`, { method: 'POST', body: { purpose: props.purpose, itemId: props.itemId, target: props.target } }); if (alive) { challenge.value = result; context.value = result } }
  catch (cause) { if (alive) { error.value = messageOf(cause); unavailable.value = cause.code === 'MOTP_UNAVAILABLE' } }
  finally { if (alive) busy.value = false }
}
// 휴대폰·아이핀 인증: 사용자가 누른 버튼으로만 NICE 팝업을 연다. 결과는 팝업의 성공 선언이 아니라 서버 결과로 확인한다.
async function alternative(method) {
  if (busy.value || !context.value || nice.value) return
  popup = window.open('', 'learning-identity', 'width=500,height=650')
  if (!popup) { error.value = '본인인증 팝업을 허용한 뒤 다시 시도해 주세요.'; return }
  busy.value = true; error.value = ''
  try {
    const value = await props.request(`/api/user/enrollments/${props.enrollId}/identity/nice/prepare`, { method: 'POST', body: { motpRequestId: context.value.requestId, method } })
    if (!alive) { popup.close(); return }
    nice.value = value
    const form = document.createElement('form'); form.method = 'POST'; form.action = value.url; form.target = 'learning-identity'
    for (const [name, data] of Object.entries({ m: 'service', token_version_id: value.tokenVersionId, enc_data: value.encData, integrity_value: value.integrityValue })) { const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = data; form.append(input) }
    document.body.append(form); form.submit(); form.remove(); polling = setInterval(checkAlternative, 2500)
  } catch (cause) {
    if (alive) { error.value = messageOf(cause); if (['MOTP_REQUEST_EXPIRED', 'MOTP_REQUEST_BUSY', 'MOTP_CONTEXT_CHANGED', 'MOTP_RATE_LIMITED'].includes(cause.code)) retry.value = true }
    popup?.close()
  } finally { if (alive) busy.value = false }
}
async function checkAlternative() {
  if (!alive || busy.value || !nice.value) return
  busy.value = true
  try {
    const result = await props.request(`/api/user/enrollments/${props.enrollId}/identity/nice/${nice.value.id}/result`)
    if (!alive) return
    if (result.state === 'SUCCEEDED' && result.identity?.result === 'SUCCESS') { clearInterval(polling); popup?.close(); nice.value = null; emit('verified') }
    else if (['FAILED', 'UNKNOWN', 'REJECTED', 'EXPIRED'].includes(result.state)) {
      clearInterval(polling); nice.value = null; retry.value = true
      error.value = result.code === 'MEMBER_MISMATCH' ? messageOf({ code: 'NICE_MEMBER_MISMATCH' }) : '본인인증에 실패했거나 결과를 확인하지 못했습니다. 다시 인증해 주세요.'
    }
  } catch (cause) { if (alive) { error.value = messageOf(cause); clearInterval(polling); nice.value = null } }
  finally { if (alive) busy.value = false }
}
async function verify() {
  if (busy.value || !challenge.value) return
  if (!/^\d{6}$/.test(otp.value)) { error.value = 'OTP는 6자리를 입력해야 합니다.'; return }
  busy.value = true; error.value = ''
  try {
    const result = await props.request(`${base}/verify`, { method: 'POST', body: { requestId: challenge.value.requestId, otp: otp.value } })
    otp.value = ''
    if (!alive) return
    if (result.code === '200' && result.identity?.result === 'SUCCESS') { challenge.value = null; emit('verified'); return }
    error.value = messageOf({ code: `MOTP_${result.code}` }); retry.value = true; challenge.value = null; fallback.value = !!result.alternativeRequired
  } catch (cause) {
    if (alive) { error.value = messageOf(cause); if (cause.status && cause.status < 500) { otp.value = ''; challenge.value = null; retry.value = true } }
  } finally { if (alive) busy.value = false }
}
onMounted(prepare)
onBeforeUnmount(() => { alive = false; otp.value = ''; clearInterval(polling); popup?.close() })
</script>
<template>
  <section class="motp-panel" aria-label="학습 본인인증">
    <h3>본인인증</h3>
    <p>올바른 수강을 위해 보안절차를 거치고 있습니다. <strong>mOTP 인증</strong> 또는 <strong>휴대폰·아이핀 본인인증</strong> 중 하나를 선택해 인증해 주세요.</p>
    <p v-if="busy" role="status">인증 요청을 확인하고 있습니다.</p><p v-if="error" role="alert">{{ error }}</p>
    <iframe v-if="context?.fdsUrl" :src="context.fdsUrl" title="원격훈련 본인인증 보안 확인" referrerpolicy="no-referrer" hidden />
    <div v-if="context" class="motp-methods">
      <div class="motp-method" :class="{ dim: fallback }">
        <h4>mOTP 인증</h4>
        <p>한국산업인력공단 mOTP 앱에 표시된 OTP 비밀번호 6자리를 입력해 주세요. 앱에 등록한 이름·휴대폰 번호가 회원정보와 같아야 합니다.</p>
        <form v-if="challenge" @submit.prevent="verify"><label>OTP 비밀번호 입력<input v-model="otp" type="password" inputmode="numeric" pattern="[0-9]{6}" maxlength="6" autocomplete="off" :disabled="busy" /></label><button type="submit" :disabled="busy || !otp">입력확인</button></form>
        <button v-else-if="!nice" :disabled="busy" @click="prepare">새 OTP 비밀번호로 다시 인증</button>
        <a :href="MOTP_GUIDE" target="_blank" rel="noopener noreferrer">mOTP 매뉴얼 및 설치 안내</a>
      </div>
      <div v-if="alternatives.length" class="motp-method" :class="{ focus: fallback }">
        <h4>휴대폰·아이핀 본인인증</h4>
        <p>OTP 비밀번호 연속 오류(5회 이상) 또는 OTP를 사용할 수 없는 경우 본인인증을 해 주시기 바랍니다.</p>
        <div class="motp-buttons"><button v-for="method in alternatives" :key="method" :disabled="busy || !!nice" @click="alternative(method)">{{ method === 'MOBILE' ? '휴대폰 인증' : '아이핀 인증' }}</button></div>
        <p v-if="nice" role="status">본인인증 창에서 인증을 마치면 자동으로 확인합니다. <button :disabled="busy" @click="checkAlternative">인증 결과 확인</button></p>
        <p class="hint">※ 타인 명의 휴대폰인 경우 아이핀 인증으로 진행해 주시기 바랍니다.</p>
      </div>
    </div>
    <button v-else-if="!unavailable" :disabled="busy" @click="prepare">인증 요청 다시 확인</button>
    <p class="hint">인증에 실패하거나 결과를 확인하지 못하면 학습·평가를 진행할 수 없습니다. 계속 실패하면 고객센터로 문의해 주세요.</p>
  </section>
</template>
<style scoped>
.motp-panel{padding:18px;border:1px solid #c8dce7;border-radius:10px;background:#f5fafc;margin:18px 0;overflow-wrap:anywhere}.motp-panel h3{font-size:18px}.motp-panel h4{font-size:16px;margin-bottom:6px}.motp-panel p{line-height:1.7;margin:10px 0}
.motp-methods{display:grid;grid-template-columns:1fr;gap:12px;margin:14px 0}.motp-method{padding:14px;border:1px solid #d5e3ec;border-radius:8px;background:white}.motp-method.dim{opacity:.7}.motp-method.focus{border-color:#187d95;box-shadow:0 0 0 2px #cfe8ef}
.motp-panel label{display:block;font-weight:600}.motp-panel input{display:block;border:1px solid #95b2c4;padding:12px;border-radius:6px;width:100%;max-width:240px;background:white;margin:8px 0}
.motp-panel button{background:#187d95;color:white;padding:10px 16px;border-radius:6px;margin:6px 8px 6px 0}.motp-panel button:disabled{opacity:.5}.motp-buttons{display:flex;flex-wrap:wrap}
.motp-panel a{display:inline-block;margin-top:6px;color:#187d95;text-decoration:underline}.motp-panel .hint{font-size:13px;color:#5b6b78}.motp-panel [role=alert]{color:#a22828}.motp-panel :focus-visible{outline:3px solid #337ec3;outline-offset:2px}
</style>
